import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { stripeApi } from "../../api/stripe";
import marginLogo from "../../assets/vectorlogo.png";
import "../../components/Checkout/CheckoutPanel.css";
import "./CheckoutSuccess.css";

interface Credentials {
  username: string;
  password: string;
  token: string;
  userId: number;
}

const CheckoutSuccess: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");

  const [creds, setCreds] = useState<Credentials | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [copied, setCopied] = useState<"username" | "password" | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setError("Нет session_id в адресе");
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    let attempt = 0;
    const maxAttempts = 8;

    const tryFetch = async () => {
      try {
        const { data } = await stripeApi.credentials(sessionId);
        setCreds(data);
        setRetrying(false);
        localStorage.setItem("token", data.token);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 409 && attempt < maxAttempts) {
          setRetrying(true);
          attempt += 1;
          setTimeout(tryFetch, 1500 * attempt);
          return;
        }
        if (status === 410) {
          setError("Эти данные уже были получены ранее");
        } else if (status === 404) {
          setError("Сессия не найдена");
        } else {
          setError("Не удалось получить данные");
        }
      }
    };

    tryFetch();
  }, [sessionId]);

  const handleCopy = (kind: "username" | "password", value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(kind);
      setTimeout(() => setCopied(null), 1400);
    });
  };

  return (
    <div className="mc-page">
      <div className="mc-page__inner mc__panel">
          <div className="mc__brand">
            <img src={marginLogo} alt="lovestoria" />
          </div>

          {!creds && !error && (
            <div className="mc__heading">
              <h1 className="mc__title">
                {retrying ? "Подтверждаем оплату" : "Создаём аккаунт"}
              </h1>
            </div>
          )}

          {error && (
            <>
              <div className="mc__heading">
                <h1 className="mc__title">Ошибка</h1>
                <div className="mc__error">{error}</div>
              </div>
              <button
                type="button"
                className="mc__submit mc__submit--ghost"
                onClick={() => navigate("/login")}
              >
                Перейти ко входу
              </button>
            </>
          )}

          {creds && (
            <>
              <div className="mc__heading">
                <h1 className="mc__title">Оплата принята</h1>
              </div>

              <div className="mc-creds">
                <div className="mc-creds__row">
                  <div className="mc__label">Логин</div>
                  <div className="mc-creds__line">
                    <span className="mc-creds__value">{creds.username}</span>
                    <button
                      type="button"
                      className="mc-creds__copy"
                      onClick={() => handleCopy("username", creds.username)}
                    >
                      {copied === "username" ? "Скопировано" : "Скопировать"}
                    </button>
                  </div>
                </div>
                <div className="mc-creds__row">
                  <div className="mc__label">Пароль</div>
                  <div className="mc-creds__line">
                    <span className="mc-creds__value mc-creds__value--mono">
                      {creds.password}
                    </span>
                    <button
                      type="button"
                      className="mc-creds__copy"
                      onClick={() => handleCopy("password", creds.password)}
                    >
                      {copied === "password" ? "Скопировано" : "Скопировать"}
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="mc__submit"
                onClick={() => navigate("/forms", { replace: true })}
              >
                Перейти к книге
              </button>
            </>
          )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
